import { shallowStringify } from '@gozoki/tools'

import type { WorksConcil } from '@gozoki/api-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { frenchNumberSchema } from '../tools/zod-schemas'

export const zWorksConcilForm = z
  .object({
    // Works concil label (max 30 to match backend `rules.maxLength(30)`)
    label: z
      .string()
      .min(3, 'Le libellé du partenariat doit faire au minimum 3 caractères')
      .max(30, 'Le libellé du partenariat doit faire au maximum 30 caractères'),

    companyLabel: z.string().nonempty({ message: "Le nom de l'entreprise est requis" }),
    companyCity: z.string().nonempty({ message: "La ville de l'entreprise est requise" }),
    companyAddress: z.string().nullable(),
    companySiren: z
      .string()
      .nonempty({ message: "Le SIREN de l'entreprise est requis" })
      .regex(/^\d{9}$/, 'Le SIREN doit comporter exactement 9 chiffres'),

    // Works concil financial informations (mirror backend constraints to fail-fast client-side)
    maxMonthAmountPerEmployee: frenchNumberSchema.nullable().refine((v) => v === null || v >= 0, {
      message: 'Le budget mensuel par salarié ne peut pas être négatif',
    }),
    gozokiPercentage: frenchNumberSchema
      .nullable()
      .refine((v) => v === null || (v >= 0 && v <= 30), {
        message: 'Le pourcentage Gozoki doit être compris entre 0 et 30 %',
      }),
    worksConcilPercentage: frenchNumberSchema
      .nullable()
      .refine((v) => v === null || (v >= 0 && v <= 30), {
        message: 'Le pourcentage CE doit être compris entre 0 et 30 %',
      }),

    // Works concil dates
    startDate: z.string().nonempty({ message: 'La date de début est requise' }),
    endDate: z.string().nonempty({ message: 'La date de fin est requise' }),

    // Works concil users
    employees: z.array(z.number()).nullable().default([]),
    emails: z.array(z.email()).nullable().default([]),
    emailsToAdd: z.array(z.email('Une adresse email saisie est invalide')).nullable().default([]),

    // Works concil status
    isActive: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    // Cross-field: end date must be after or equal to start date.
    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'La date de fin doit être postérieure ou égale à la date de début',
      })
    }

    // Cross-field: if a CE percentage > 0 is set, monthly amount per employee must be > 0.
    const ceRate = data.worksConcilPercentage ?? 0
    if (
      ceRate > 0 &&
      (data.maxMonthAmountPerEmployee == null || data.maxMonthAmountPerEmployee <= 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['maxMonthAmountPerEmployee'],
        message:
          'Le budget mensuel par salarié est requis et doit être supérieur à 0 quand un pourcentage CE est défini',
      })
    }
  })

export type WorksConcilFormInput = z.input<typeof zWorksConcilForm>
export type WorksConcilFormOutput = z.output<typeof zWorksConcilForm>

// Form hook
export const useWorksConcilForm = (defaultValues?: Partial<WorksConcilFormInput>) => {
  const form = useForm({
    // Validation function
    defaultValues: {
      companyLabel: '',
      companyCity: '',
      companyAddress: '',
      companySiren: '',
      startDate: DateTime.now().toISODate() as string,
      gozokiPercentage: 0,
      worksConcilPercentage: 0,
      maxMonthAmountPerEmployee: null,
      employees: [],
      isActive: false,
      ...defaultValues,
    },
    resolver: zodResolver(zWorksConcilForm),
  })

  return form
}

// Helper : generate precompleted form from existing product data for modification
// Only extract the fields declared in the schema — avoids leaking large preloaded
// relations (e.g. `credits`) into the form state, which would balloon PUT payloads.
export const fromExistingWorksConcil = async (
  worksConcil: WorksConcil,
): Promise<WorksConcilFormOutput> =>
  shallowStringify({
    label: worksConcil.label,
    companyLabel: worksConcil.companyLabel,
    companyCity: worksConcil.companyCity,
    companyAddress: worksConcil.companyAddress,
    companySiren: worksConcil.companySiren,
    maxMonthAmountPerEmployee: worksConcil.maxMonthAmountPerEmployee,
    gozokiPercentage: worksConcil.gozokiPercentage,
    worksConcilPercentage: worksConcil.worksConcilPercentage,
    startDate: worksConcil.startDate,
    endDate: worksConcil.endDate,
    isActive: worksConcil.isActive,
    employees: worksConcil.employees.map((employee) => employee.id),
    emails: worksConcil.emails,
    emailsToAdd: null,
  })
